import { defineStore, storeToRefs } from 'pinia';
import { ref, watch } from 'vue';
import { RouteOption } from '@/router/interface';
import { addRoutes, removeRoute } from '@/router/dynamicRoutes';
import { useSettingStore } from './setting';
import { RouteRecordRaw, RouteMeta } from 'vue-router';
import { useAuthStore } from '@/plugins';
import router from '@/router';
import { set } from 'nprogress';

export interface MenuProps {
  id?: number;
  name: string;
  path: string;
  title?: string;
  icon?: string;
  badge?: number | string;
  target?: '_self' | '_blank';
  link?: string;
  component: string;
  renderMenu?: boolean;
  permission?: string;
  parent?: string;
  children?: MenuProps[];
  cacheable?: boolean;
  view?: string;
}

/**
 * 过滤菜单
 * @param routes
 * @param parentPermission
 */
function doMenuFilter(routes: Readonly<RouteRecordRaw[]>, parentPermission?: string) {
  const { hasAuthority } = useAuthStore();

  const setCache = (meta: RouteMeta) => {
    meta._cache = {
      renderMenu: meta.renderMenu,
    };
  };

  routes.forEach((route) => {
    const required = route.meta?.permission ?? parentPermission;
    // if (route.meta?.renderMenu === undefined && required) {
    if (required) {
      route.meta = route.meta ?? {};
      setCache(route.meta);
      route.meta.renderMenu = hasAuthority(route.meta.permission);
    }
    if (route.children) {
      doMenuFilter(route.children, required);
    }
  });
}

/**
 * 重置过滤
 * @param routes
 */
function resetMenuFilter(routes: Readonly<RouteRecordRaw[]>) {
  const resetCache = (meta: RouteMeta) => {
    if (meta._cache) {
      meta.renderMenu = meta._cache?.renderMenu;
    }
    delete meta._cache;
  };
  routes.forEach((route) => {
    if (route.meta) {
      resetCache(route.meta);
    }
    if (route.children) {
      resetMenuFilter(route.children);
    }
  });
}

// 菜单数据转为路由数据
const toRoutes = (list: MenuProps[]): RouteOption[] => {
  return list.map((item) => ({
    name: item.name,
    path: item.path,
    component: item.component,
    children: item.children && toRoutes(item.children),
    meta: {
      title: item.title,
      permission: item.permission,
      icon: item.icon,
      renderMenu: item.renderMenu,
      cacheable: item.cacheable,
      href: item.link,
      badge: /^(false|true)$/i.test(item.badge + '') ? JSON.parse(item.badge + '') : item.badge,
      target: item.target,
      view: true,
    },
  }));
};

export const useMenuStore = defineStore('menu', () => {
  const menuList = ref<MenuProps[]>(JSON.parse(window.localStorage.getItem('menuList') || '[]'));
  const loading = ref(true);
  loading.value = true;
  const { filterMenu } = storeToRefs(useSettingStore());
  const checkMenuPermission = () => {
    if (filterMenu.value) {
      doMenuFilter(router.options.routes);
    } else {
      resetMenuFilter(router.options.routes);
    }
  };

  // checkMenuPermission();

  watch(filterMenu, checkMenuPermission);

  const restoreCurrentDynamicRoute = () => {
    const current = router.currentRoute.value;
    if (!current?.meta?._is404Page) {
      return;
    }
    const resolved = router.resolve(current.fullPath);
    const shouldReplace =
      !resolved.meta?._is404Page &&
      (resolved.name !== current.name || resolved.matched.length !== current.matched.length);
    if (shouldReplace) {
      router.replace(current.fullPath);
    }
  };

  // 手动注册固定路由，不受接口控制
  const addStaticRoutes = () => {
    if (!router.hasRoute('帮助与公告')) {
      router.addRoute({
        path: '/help',
        name: '帮助与公告',
        meta: { renderMenu: false, cacheable: false },
        component: () => import('@/pages/help/index.vue'),
      });
    }
    if (!router.hasRoute('费收费标准公示')) {
      router.addRoute({
        path: '/help/fee-standard',
        name: '费收费标准公示',
        meta: { renderMenu: false, cacheable: false },
        component: () => import('@/pages/help/fee-standard.vue'),
      });
    }
    if (!router.hasRoute('售后规则')) {
      router.addRoute({
        path: '/help/aftersale-rules',
        name: '售后规则',
        meta: { renderMenu: false, cacheable: false },
        component: () => import('@/pages/help/aftersale-rules.vue'),
      });
    }
  };

  async function getMenuList(data) {
    window.localStorage.setItem('menuList', JSON.stringify(data));
    addRoutes(toRoutes(data));
    addStaticRoutes();
    menuList.value = data;
    checkMenuPermission();
    restoreCurrentDynamicRoute();
    loading.value = false;
  }

  const addRoute = () => {
    addRoutes(toRoutes(menuList.value));
    addStaticRoutes();
    checkMenuPermission();
    restoreCurrentDynamicRoute();
    setTimeout(() => {
      loading.value = false;
    }, 1200);
  };

  const setMenu = (data) => {
    removeRoute(menuList.value);
    menuList.value = data;
  };

  return {
    loading,
    menuList,
    getMenuList,
    addRoute,
    setMenu,
  };
});
